import { BusinessLogic } from './businessLogic'
import { MilestoneCameraAttributes } from './camera'
import { NotificationSettings } from './notificationSettings'

export type DeploymentDeviceFilterOption = {
  id: string
  name: string
}

export type DeploymentConfig = {
  // Common fields
  application_id: string
  primary_model_id: string
  secondary_model_id?: string
  gpu_index?: number
  run_in_groups?: boolean
  gpu_selection?: GpuSelectionEntry[]
  group_settings?: GroupSettingsPayload
  confidence_threshold: ConfidenceThreshold[]
  blur_options: BlurOptions
  input_source: InputSource

  // Camera-only fields (to be moved inside CameraInputSource in a future refactoring)
  config_width?: number
  config_height?: number
  event_video_duration_time?: number
  min_detection_frames?: number
  notification_settings?: NotificationSettings
  ocr_settings?: OcrSettings
}

export type GpuSelectionEntry = {
  device_id: string
  gpu_indexes: number[]
}

export type GpuSelection = Record<string, number[]>

export type GroupSettingsPayload = {
  precision?: 'fp16' | 'fp32'
  interval?: number
  pre_event_s?: number
  cache_s?: number | null
}

// Not served by the API yet (BE-7); the editor prefills from it when present.
export type DeploymentAppSettings = {
  run_in_groups: boolean
  precision: 'fp16' | 'fp32'
  interval: number
  pre_event_s: number
  post_event_s: number
  cache_s?: number | null
}

export type DeploymentPlanMode = 'create' | 'update'

export type DeploymentPlanDisabledReason = 'groups_disabled' | 'app_switch_off'

export type DeploymentPlanNotSupportedReason =
  | DeploymentPlanDisabledReason
  | 'device_not_capable'
  | 'jetson_device'
  | 'no_group_image'
  | 'driver_too_old'

export type GroupExclusionReason =
  | 'blur_enabled'
  | 'bbox_video'
  | 'live_output'
  | 'non_rtsp_source'
  | 'media_folder'
  | 'static_batch_model'
  | 'unsupported_model_type'
  | 'multiple_milestone_servers'
  | 'jetson_device'
  | 'device_not_capable'
  | 'no_group_image'
  | 'driver_too_old'
  | 'groups_disabled'

export type DeploymentPlanGroupAction = 'keep' | 'update' | 'create' | 'remove'

export type DeploymentPlanMemberChange =
  'unchanged' | 'updated' | 'added' | 'moved_in' | 'moved_out' | 'removed'

export type DeploymentPlanCreatedBecause =
  | 'first_group'
  | 'groups_full'
  | 'frame_size_bucket'
  | 'milestone_server'
  | 'settings_changed'

export type DeploymentPlanDeviceWarning =
  | 'gpu_load_high'
  | 'groups_exceed_gpu_memory'
  | 'no_allowed_gpu'
  | 'gpu_shared_with_vlm'
  | 'gpu_memory_estimate_exceeds_capacity'

export type DeploymentPlanGroupSettings = {
  precision: 'fp16' | 'fp32'
  interval: number
  frame_width: number
  frame_height: number
  recording_mode: string
  pre_event_s: number
  post_event_s: number
  cache_s?: number | null
}

export type DeploymentPlanMember = {
  stream_id: string
  camera_name: string
  deployment_id: string | null
  stream_index: number | null
  change: DeploymentPlanMemberChange
}

export type DeploymentPlanGroup = {
  group_id: string | null
  display_number: number | null
  action: DeploymentPlanGroupAction
  tracked: boolean
  revision_next: number | null
  capacity: number
  created_because: DeploymentPlanCreatedBecause | null
  settings: DeploymentPlanGroupSettings
  restarts_container: boolean
  members: DeploymentPlanMember[]
}

export type DeploymentPlanSingleCamera = {
  stream_id: string
  camera_name: string
  deployment_id: string | null
  reasons: GroupExclusionReason[]
}

export type DeploymentPlanLoad = {
  pct: number
  per_gpu: Record<string, number>
  frames_per_s: number
  runs_per_s: number
}

export type DeploymentPlanMemory = {
  fit: number
  needed: number
  per_gpu: Record<string, number>
  estimate_mb: number
}

export type DeploymentPlanDevice = {
  device_id: string
  groups_supported: boolean
  not_supported_reason: DeploymentPlanNotSupportedReason | null
  allowed_gpus: number[]
  capacity: number
  warnings: DeploymentPlanDeviceWarning[]
  load?: DeploymentPlanLoad | null
  memory?: DeploymentPlanMemory | null
  groups: DeploymentPlanGroup[]
  single_camera: DeploymentPlanSingleCamera[]
  paused: DeploymentPlanSingleCamera[]
}

export type DeploymentPlanSummary = {
  cameras: number
  groups: number
  groups_removed: number
  single_camera: number
  paused: number
  containers_restarted: number
}

export type DeploymentPlan = {
  application_id: string
  mode: DeploymentPlanMode
  groups_enabled: boolean
  disabled_reason: DeploymentPlanDisabledReason | null
  devices: DeploymentPlanDevice[]
  summary: DeploymentPlanSummary
}

type StreamConfig = {
  stream_id: string
  device_id: string
  frame_width: number
  frame_height: number
  eglsink: boolean
  rtspsink: boolean
  business_logics: BusinessLogic[]
  event_endpoints: EventEndpoint[]
}

export enum EventDestination {
  Visionplatform = 'visionplatform',
  CustomApi = 'custom_api',
  Milestone = 'milestone',
  Mqtt = 'mqtt'
}

export type VisionplatformEventEndpoint = {
  service: EventDestination.Visionplatform
}

export type CustomApiEventEndpoint = {
  service: EventDestination.CustomApi
  endpoint: string
}

export type MilestoneEventEndpoint = {
  service: EventDestination.Milestone
  endpoint: string
  attributes: MilestoneCameraAttributes
}

export type MqttEventEndpoint = {
  service: EventDestination.Mqtt
  attributes: MqttAttributes
}

export type EventEndpoint =
  | VisionplatformEventEndpoint
  | CustomApiEventEndpoint
  | MilestoneEventEndpoint
  | MqttEventEndpoint

export type MqttAttributes = {
  broker_address: string
  broker_port: number
  broker_qos: number
  topics: MqttTopic[]
}

export type MqttTopic = {
  type: 'periodic_log' | 'event_log'
  name: string
  payload_format: MqttPayloadFormat
  interval?: number
}

export enum MqttPayloadFormat {
  Default = 0,
  Oinsights = 1
}

type CommonSettingsPayload = {
  confidence_threshold: ConfidenceThreshold[]
  blur_options: BlurOptions
}

export type CameraSettingsPayload = StreamConfig &
  CommonSettingsPayload & {
    input_source_type: InputSourceType.Cameras
    event_video_duration_time: number
    min_detection_frames: number
  }

export type MediaFolderSettingsPayload = MediaFolderConfig &
  CommonSettingsPayload & {
    input_source_type: InputSourceType.MediaFolder
    device_id: string
  }

export type Deployment = {
  id: string
  application_id: string
  application_name: string
  primary_ml_model: {
    id: string
    name: string
  }
  secondary_ml_model: {
    id: string
    name: string
  } | null
  gpu_index?: number | null
  group_id?: string | null
  allowed_gpus?: number[] | null
  stream_index?: number | null
  deployment_settings?: DeploymentAppSettings | null
  gpu?: DeploymentGpu | null
  group_exclusion_reasons?: GroupExclusionReason[]
  single_camera_reasons?: GroupExclusionReason[]
  settings_payload: CameraSettingsPayload | MediaFolderSettingsPayload | null

  // Camera-only fields (to be moved inside CameraSettingsPayload in a future refactoring)
  notification_settings?: NotificationSettings
  ocr_settings?: OcrSettings

  status: DeploymentStatus
  status_display: string
  status_updated_at: string | null
  error_body: string
  created_at: string
}

export type DeploymentGpu = {
  index: number
  uuid: string | null
  name: string
}

export enum InputSourceType {
  Cameras = 'cameras',
  MediaFolder = 'media_folder'
}

export type MediaFolderConfig = {
  input_folder_path: string
  output_folder_path: string
  delete_processed: boolean
}

export type CameraInputSource = {
  type: InputSourceType.Cameras
  streams: StreamConfig[]
}

export type MediaFolderInputSource = MediaFolderConfig & {
  type: InputSourceType.MediaFolder
  device_id: string
}

export type InputSource = CameraInputSource | MediaFolderInputSource

export enum DeploymentStatus {
  InProgress = 1,
  Inactive = 2,
  Running = 3,
  Failed = 4,
  Removing = 5,
  Stopping = 6,
  Stopped = 7,
  // Fine-grained pipeline startup statuses reported by the DeepStream app.
  // Must stay in sync with the backend Deployment.STATUS_CHOICES integers.
  Initializing = 8,
  LoadingModel = 9,
  PreparingOcr = 10,
  RegisteringMilestone = 11,
  ConnectingVideoStream = 12,
  DownloadingConfig = 13,
  DownloadingModel = 14,
  CheckingDockerManifest = 15,
  DownloadingImage = 16,
  // A previously-failed deployment whose container is restarting and walking
  // the startup sequence again. Derived by the edge client; its error_body
  // carries the live startup phase plus the preserved last failure.
  Recovering = 17,
  QueuedForEngineBuild = 18,
  BuildingEngine = 19,
  // Reported for deployment groups only: the container runs but some cameras do not.
  Degraded = 20
}

export type ConfidenceThreshold = {
  label_id: string
  threshold: number
}

export type BlurOptions = {
  blur_face: boolean
  blur_lp: boolean
  blur_label_ids: string[]
  blur_transparency: number
}

export type OcrSettings = {
  whitelist: string[]
  blacklist: string[]
  detector: boolean
}
